import { useState } from "react";

type ExpandedSection = "examples" | "howItWorks" | null;

/**
 * Expandable component showing example answers and how it works.
 * Only shown on BrainstormLanding page.
 * Only one section can be expanded at a time.
 */
export function ExampleAnswers() {
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>(null);

  const toggleSection = (section: ExpandedSection) => {
    setExpandedSection((current) => (current === section ? null : section));
  };

  const isExamplesExpanded = expandedSection === "examples";
  const isHowItWorksExpanded = expandedSection === "howItWorks";

  return (
    <div className="mt-4 font-sans">
      {/* Links */}
      <div className="flex items-center justify-center gap-2 text-sm text-base-400">
        <button
          onClick={() => toggleSection("examples")}
          className={`hover:opacity-70 transition-opacity ${
            isExamplesExpanded ? "text-base-500 underline" : "text-base-400"
          }`}
        >
          See examples of answers
        </button>
        <span className="opacity-70">•</span>
        <button
          onClick={() => toggleSection("howItWorks")}
          className={`hover:opacity-70 transition-opacity ${
            isHowItWorksExpanded ? "text-base-500 underline" : "text-base-400"
          }`}
        >
          Learn how it works
        </button>
      </div>

      {/* Examples expandable content */}
      <div
        data-testid="examples-panel"
        data-expanded={isExamplesExpanded}
        className={`overflow-hidden transition-all duration-300 ease-out ${
          isExamplesExpanded ? "max-h-[400px] opacity-100 mt-4" : "max-h-0 opacity-0"
        }`}
      >
        <div
          className="border border-neutral-200 rounded-2xl px-6 py-6 mx-auto"
          style={{ maxWidth: "703px" }}
        >
          <div className="text-xs text-base-500 leading-relaxed">
            <p className="mb-0">
              <span className="font-semibold">Example structure:</span>
            </p>
            <p className="mb-4">
              [Business Name] is a [type of business] that [does what] for [target customer]. I want to [marketing goal].
            </p>

            <p className="font-semibold mb-1">Examples:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>
                Rapid Plumbing is a plumbing company in Austin, TX. We handle emergency repairs
                and want to get more calls from homeowners.
              </li>
              <li>
                QuotaHit is a sales enablement platform for content management and sales coaching.
                We want to generate demo requests from mid-market sales teams.
              </li>
              <li>
                I'm launching a meal prep delivery service for busy professionals in Denver.
                I want to validate demand before investing in a commercial kitchen.
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* How it works expandable content */}
      <div
        data-testid="how-it-works-panel"
        data-expanded={isHowItWorksExpanded}
        className={`overflow-hidden transition-all duration-300 ease-out ${
          isHowItWorksExpanded ? "max-h-[400px] opacity-100 mt-4" : "max-h-0 opacity-0"
        }`}
      >
        <div
          className="border border-neutral-200 rounded-2xl px-6 py-6 mx-auto"
          style={{ maxWidth: "703px" }}
        >
          <ol className="text-xs text-base-500 leading-relaxed list-decimal ml-4 space-y-1">
            <li>Describe your business and goals — we'll ask a few follow-up questions to get the details right.</li>
            <li>
              We build your landing page, Google Ads campaign, and conversion tracking — all connected.
            </li>
            <li>
              Once you run your ad, see exactly what's working: cost per lead, which keywords convert, and where to improve.
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
